import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { ApContentService } from '@ap/content';
import { ApModalService, ModalClosedReason, ModalOptions } from '@ap/modal';
import { ApPersistenceService } from '@ap/persistence';

import { MijnTokensBrowserAnalyticsService } from '../analytics/analytics.service';
import { LogoutModalComponent } from '../logout-modal/logout-modal.component';
import { BrowserSessionName, BrowserSessionType } from '../mijn-tokens-browser.constants';
import { BrowserTokensSession } from '../mijn-tokens-browser.model';
import { MijnTokensBrowserNavigation } from '../mijn-tokens-browser.navigation';
import { MijnTokensBrowserService } from '../mijn-tokens-browser.service';

import content from './device.wrapper.content.json';

/**
 * @module RegisterDeviceWrapperModule
 * @ngdoc component
 * @name DeviceWrapperComponent
 */
@Component({
    selector: 'tokens-browser-device-wrapper',
    templateUrl: 'device.wrapper.component.html',
    standalone: false
})
export class DeviceWrapperComponent implements OnInit, OnDestroy {
    public device: string;

    private readonly subscription: Subscription = new Subscription();

    public constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly analyticsService: MijnTokensBrowserAnalyticsService,
        private readonly navigation: MijnTokensBrowserNavigation,
        private readonly mijnTokensBrowserService: MijnTokensBrowserService,
        private readonly modalService: ApModalService,
        private readonly persistenceService: ApPersistenceService,
        private readonly contentService: ApContentService
    ) {}

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.navigation.init(this.activatedRoute.parent);
        this.sendFormStepAnalytics();
        this.device = this.mijnTokensBrowserService.getDevice();
        this.persistenceService.clearSession(BrowserSessionName);
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public onNewDevice(device: string): void {
        this.mijnTokensBrowserService.canContinueBrowserRegistration().subscribe({
            next: (isLoggedOut: boolean) => {
                isLoggedOut ? this.registerBrowser(device) : this.showLogoutModal(device);
            }
        });
    }

    public onBack(device: string): void {
        this.mijnTokensBrowserService.setDevice(device);
        this.navigation.toName();
    }

    private showLogoutModal(device: string): void {
        const modalOptions: ModalOptions<any> = this.mijnTokensBrowserService.getModalOptions();

        const modalComponent = this.modalService.open(LogoutModalComponent, modalOptions);
        this.subscription.add(
            modalComponent.afterClosed().subscribe((result) => {
                if (result.reason === ModalClosedReason.ACCEPTED) {
                    this.mijnTokensBrowserService.terminateUserSession().subscribe({
                        next: () => this.registerBrowser(device)
                    });
                }
            })
        );
    }

    private registerBrowser(device: string): void {
        this.mijnTokensBrowserService.setDevice(device);
        this.mijnTokensBrowserService.registerBrowser().subscribe((storage: BrowserTokensSession) => {
            this.storeTokens(storage);
            const url = this.contentService.get('mijn.tokens.browser.identify');
            this.navigation.external(url);
        });
    }

    private sendFormStepAnalytics(): void {
        this.analyticsService.pushFormStepAnalytics(MijnTokensBrowserNavigation.DEVICE, 4, 'start');
    }

    private storeTokens(storage: BrowserTokensSession): void {
        this.persistenceService.setSession(BrowserSessionName, BrowserSessionType, storage);
    }
}
